// Garment size codes. A one-size garment (a tote, an adjustable cap) is stored, priced and ordered under the code "OS"
// and shown as "One size" everywhere a buyer or the press reads it. A fitted cap's split sizes ("S/M", "L/XL") are codes
// of their own, shown as written. The pages bundle this file too, so it imports nothing.

export const ONE_SIZE = "OS";
export const ONE_SIZE_LABEL = "One size";

// How S&S and older pages spell one size: "One Size", "OSFA", "OS", S&S's "Adjustable" for a strap-back cap, and the
// "One size" the buyer's-own tote used before this code. Compared without case, spaces or hyphens.
const ONE_SIZE_SPELLINGS = new Set(["os", "osfa", "onesize", "onesizefitsall", "adjustable"]);
const isOneSize = (size: string) => ONE_SIZE_SPELLINGS.has(size.replace(/[\s-]+/g, "").toLowerCase());

// A fitted cap's split size: two letter sizes joined by a slash, like "XS/S", "S/M", "M/L", "L/XL" or "XL/2XL".
const SPLIT_SIZE = /^\d?X{0,3}[SML]\/\d?X{0,3}[SML]$/;
const PLAIN_SIZE = /^[A-Za-z0-9]{1,8}$/;

// The code a size is kept under: any one-size spelling becomes ONE_SIZE, a split size is written in capitals without
// spaces ("m / l" is "M/L"), every other size is unchanged.
export function sizeCode(size: string): string {
  if (isOneSize(size)) return ONE_SIZE;
  const split = size.replace(/\s+/g, "").toUpperCase();
  return SPLIT_SIZE.test(split) ? split : size;
}

// A size code the order pages, the order API and the importer take: 1 to 8 letters or digits, or a split cap size.
// Checked on the code (sizeCode), so "One Size" and "s/m" pass as OS and S/M.
export function isSizeCode(size: string): boolean {
  return PLAIN_SIZE.test(size) || SPLIT_SIZE.test(size);
}

// Letter sizes in wearing order, with a cap's split sizes between the sizes they span: S/M after S, L/XL after L.
const SIZE_ORDER = ["OS", "XS", "XS/S", "S", "S/M", "M", "M/L", "L", "L/XL", "XL", "XL/2XL", "2XL", "3XL", "4XL", "5XL", "6XL"];

// Sizes in wearing order when every one is in SIZE_ORDER; any other list (youth, infant, waist sizes) keeps the
// order it came in, which is S&S's.
export function sortSizes(sizes: string[]): string[] {
  const rank = sizes.map((s) => SIZE_ORDER.indexOf(s));
  if (rank.some((r) => r === -1)) return [...sizes];
  return sizes.map((s, i) => [s, rank[i]!] as const).sort((a, b) => a[1] - b[1]).map(([s]) => s);
}

// How a size reads on a page, an email or a card: "One size" for one size, the code otherwise.
export function sizeLabel(size: string): string {
  return isOneSize(size) ? ONE_SIZE_LABEL : size;
}

// "M 6, 2XL 2" or "One size 25", sizes with no garments left out.
export function sizeCounts(quantities: Record<string, number>): string {
  return Object.entries(quantities).filter(([, n]) => n > 0).map(([size, n]) => `${sizeLabel(size)} ${n}`).join(", ");
}

// Quantities as a page or a stored request sent them, keyed by size code, so a count sent as "One size" lands on
// ONE_SIZE. Counts sent under two spellings of one size add up; anything else is left for the caller to check.
// Built through a Map, so a key like "__proto__" stays an own key the caller refuses rather than vanishing.
export function codedQuantities(quantities: Record<string, unknown>): Record<string, unknown> {
  const out = new Map<string, unknown>();
  for (const [size, n] of Object.entries(quantities)) {
    const code = sizeCode(size);
    const had = out.get(code);
    out.set(code, typeof had === "number" && typeof n === "number" ? had + n : n);
  }
  return Object.fromEntries(out);
}
